import type { TaskDetailModalStore } from "@components/widgets/Modals/ModalCommunication/store/task-detail-store";
import { t } from "@lingui/macro";
import {
  HasTaskPermissionDocument,
  type HasTaskPermissionQuery,
  type HasTaskPermissionQueryVariables,
  TaskPermissionTypeEnum,
} from "@src/__generated__/graphql";
import { FetchHelper } from "@src/helpers/apollo/fetch";
import type { AppStore } from "@src/stores/AppStore";
import { BooleanState } from "@src/utils/mobx/states/BooleanState";
import { action, computed, makeObservable, observable } from "mobx";

/** Server-side task permissions, the UI hides what the API would reject. */
export class TaskPermissionsStore {
  /** Loaded once per opened task, alongside the task itself. */
  @observable.ref canUpdate = false;
  isChecking = new BooleanState();

  private permissionFetcher = new FetchHelper<
    HasTaskPermissionQuery,
    HasTaskPermissionQueryVariables
  >(HasTaskPermissionDocument);

  constructor(
    private readonly appStore: AppStore,
    private readonly detailStore: TaskDetailModalStore,
  ) {
    makeObservable(this);
  }

  @computed get isLoading(): boolean {
    return this.permissionFetcher.isLoading.value;
  }

  /** Scoped users (clients, partners) have their own update permission. */
  @computed get updateType(): TaskPermissionTypeEnum {
    return this.appStore.authStore.isInternalUser
      ? TaskPermissionTypeEnum.Update
      : TaskPermissionTypeEnum.UpdateScoped;
  }

  @action.bound
  async load(taskId: string): Promise<void> {
    this.canUpdate = false;
    const canUpdate = await this.has(taskId, this.updateType);
    // Another task was opened meanwhile
    if (taskId !== this.detailStore.taskId.value) return;

    this.canUpdate = canUpdate;
  }

  /** Runs `onAllowed`, or explains why not. */
  @action.bound
  async runIfAllowed(
    type: TaskPermissionTypeEnum,
    onAllowed: () => void,
  ): Promise<void> {
    const taskId = this.detailStore.taskId.value;
    if (!taskId) return;
    // Scoped users only see actions the API scopes for them
    if (!this.appStore.authStore.isInternalUser) return onAllowed();
    if (type === this.updateType && this.canUpdate) return onAllowed();

    this.isChecking.on();
    const isAllowed = await this.has(taskId, type);
    this.isChecking.off();

    if (isAllowed) return onAllowed();
    this.appStore.UIStore.toast({
      title: t`Insufficient permissions`,
      status: "error",
    });
  }

  private async has(
    taskId: string,
    type: TaskPermissionTypeEnum,
  ): Promise<boolean> {
    const [data] = await this.permissionFetcher.fetch(
      { task_id: taskId, type },
      undefined,
      true,
      false,
      // Assigning someone can grant the permission, so never trust the cache
      "network-only",
    );
    return !!data?.hasTaskPermission;
  }
}
